import { useActiveBookingContext, useBookingEngineStateContext, usePropertyContext } from "@app/app/hooks";
import type { IRoomAvailability } from "@app/availability/types";
import { NightIcon } from "@app/common/components/icons/NightIcon";
import { PriceableItemPrice } from "@app/common/components/PriceableItemPrice";
import { BookableType, TBookableItem } from "@app/common/types";
import { IconPeople } from "@app/componentLibrary/icon/IconPeople";
import { Badge } from "@app/componentLibrary/ui/badge";
import { Flex } from "@app/componentLibrary/ui/flex";
import { LoadingDots } from "@app/componentLibrary/ui/LoadingDots";
import { Text, ThemeTitle } from "@app/componentLibrary/ui/text";
import { prefetchTaxAndFees } from "@app/room/queries";
import { ITaxesAndFees } from "@app/room/types";
import { TagIcon } from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const extraBedsCount = (extraBedNeeded: { [k: string]: number }) =>
    Object.values(extraBedNeeded)
        .filter((el) => el > 0)
        .reduce((acc, curr) => acc + curr, 0);

export interface BookableItemProps {
    item: TBookableItem;
    onClick: () => void;
}

export const BookableItem = ({ item, onClick }: BookableItemProps) => {
    const { isLoading, getState } = useBookingEngineStateContext();
    const { activeProperty } = usePropertyContext();
    const { activeBookingIndex } = useActiveBookingContext();
    const { t } = useTranslation(["common"]);
    const showRatePerNight = !!activeProperty?.ui_config?.show_rate_per_night;
    const perStayOrNight = t(showRatePerNight ? "night" : "stay");
    const state = getState();
    const activeBooking = (state?.bookings || [])[activeBookingIndex];

    const [taxesAndFees, setTaxesAndFees] = useState<ITaxesAndFees>();

    useEffect(() => {
        if (activeProperty?.is_tax_information_enabled && item.bookable_type === BookableType.Room && item.rates?.length > 0) {
            const activeItem = item as IRoomAvailability;
            const minRate = activeItem.rates.sort((a, b) => a.rate - b.rate)[0];

            async function getTaxesAndFees() {
                const taxes = (await prefetchTaxAndFees(
                    activeProperty?.id,
                    activeItem.id,
                    minRate.id,
                    state.bookingState.dates.fromDate,
                    state.bookingState.dates.toDate,
                    activeBooking?.adultsCount,
                    activeBooking?.childCount[1],
                    activeBooking?.childCount[2],
                    state.promoCode
                )) as unknown as ITaxesAndFees;
                setTaxesAndFees(taxes);
            }
            getTaxesAndFees();
        }
    }, []);

    return (
        <li
            data-testid={`${item.bookable_type}-${item.id}`}
            key={item.id}
            className="bg-white cursor-pointer rounded-lg p-0 flex-col items-start h-full group"
            onClick={onClick}
        >
            <div className="relative">
                <img
                    data-testid={`room-img-${item.id}`}
                    src={item.images[0].path}
                    alt={item.images[0].name}
                    className="rounded-lg group-hover:shadow-lg h-52 lg:h-64 object-cover w-full"
                />

                {item.bookable_type === BookableType.Room && item.available_rooms === 1 && (
                    <Badge className="absolute top-2 left-2 text-xs">{t("oneRoomLeft", { ns: "common" })}</Badge>
                )}
            </div>
            <Flex className="w-full pt-4 mb-3" justifyContent="between" alignItems="baseline">
                <div>
                    {item.bookable_type !== BookableType.Room && (
                        <Badge variant="muted" className="mb-3 font-medium" icon={TagIcon}>
                            {t("packageItemPage.packageDeal", { ns: "packageModule" })}
                        </Badge>
                    )}
                    <ThemeTitle size="base" className="mb-2 font-bold">
                        {t(item.id, { ns: "name" })}
                    </ThemeTitle>
                    <Text
                        className="text-sm font-normal leading-normal line-clamp-2 text-gray-500"
                        dangerouslySetInnerHTML={{ __html: t(item.id, { ns: "description" }) }}
                    />
                </div>
            </Flex>

            <div className="flex justify-between gap-x-2">
                {item.bookable_type === BookableType.Room && (
                    <div className="flex gap-x-2 flex-nowrap items-end">
                        <div className="flex gap-x-1 text-sm text-gray-500">
                            <IconPeople />
                            {item.max_occupancy}
                        </div>
                        {extraBedsCount(item.extra_bed_needed) > 0 && (
                            <div className="text-sm text-gray-500 text-nowrap">+&nbsp;{extraBedsCount(item.extra_bed_needed)}</div>
                        )}
                    </div>
                )}

                {item.bookable_type !== BookableType.Room && !!item.los && (
                    <div className="flex gap-x-4">
                        <div className="flex items-end gap-x-1 text-sm text-gray-500">
                            <NightIcon className="w-4 h-4" />
                            {item.los} {t(item.los > 1 ? "nights" : "night", { ns: "common" })}
                        </div>
                    </div>
                )}

                {isLoading ? (
                    <LoadingDots />
                ) : (
                    <PriceableItemPrice
                        className="justify-end text-end"
                        item={item.min_rate}
                        discountedPrice={item.discounted_min_rate || undefined}
                        moneyClassName="font-medium"
                        priceUnit={perStayOrNight}
                        taxesAndFees={taxesAndFees}
                        displayTaxesFirst
                    />
                )}
            </div>
        </li>
    );
};
